/**
 * Admin hooks and context for shared state and actions.
 * @module AdminHooks
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../context/AuthContext';
import { adminAPI, mediaSrc } from './AdminUtils';
import { Badge } from '../ui/badge';

const AdminContext = createContext(null);

/**
 * AdminProvider wraps the Admin dashboard and exposes state/actions via context.
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
export const AdminProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading, logout, user } = useAuth();

  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState(null);
  const [events, setEvents] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [copiedEmails, setCopiedEmails] = useState(false);
  const [updatingEvent, setUpdatingEvent] = useState(false);
  const [adminActiveTab, setAdminActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isMenuModalOpen, setIsMenuModalOpen] = useState(false);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [isLineupModalOpen, setIsLineupModalOpen] = useState(false);
  const [isDeleteClientModalOpen, setIsDeleteClientModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaActiveUpdating, setMediaActiveUpdating] = useState(false);
  const [mediaActiveMessage, setMediaActiveMessage] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [isFeaturedModalOpen, setIsFeaturedModalOpen] = useState(false);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(false);

  const [libraryList, setLibraryList] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryFilter, setLibraryFilter] = useState('all');
  const [libraryQuery, setLibraryQuery] = useState('');
  const [librarySort, setLibrarySort] = useState('-createdAt');
  const [librarySelectedIds, setLibrarySelectedIds] = useState([]);

  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState('featured');
  const [selectionEventId, setSelectionEventId] = useState('');
  const [selectionTab, setSelectionTab] = useState('uploaded');
  const [selectionFilter, setSelectionFilter] = useState('all');
  const [selectionQuery, setSelectionQuery] = useState('');
  const [selectionList, setSelectionList] = useState([]);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [selectionSelectedIds, setSelectionSelectedIds] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [siteMediaEnabled, setSiteMediaEnabledState] = useState(true);
  const [reservationsLoaded, setReservationsLoaded] = useState(false);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);
  const [emailsUnread, setEmailsUnread] = useState(0);
  const fetchEmailsUnread = useCallback(async () => {
    const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'tables', 'events', 'media', 'clients', 'notifications', 'calendar', 'music', 'stats', 'admins'] : (user?.allowedTabs || []);
    if (!allowed.includes('notifications')) { setEmailsUnread(0); return; }
    try {
      const now = Date.now();
      if (fetchEmailsUnread.last && (now - fetchEmailsUnread.last) < 30000) return;
      const mt = await adminAPI.get(`/notifications/received/metrics`);
      setEmailsUnread(mt.data?.unreadTotal || 0);
      fetchEmailsUnread.last = now;
    } catch (e) {
      setEmailsUnread(0);
    }
  }, []);
  const [emailsQueued, setEmailsQueued] = useState(0);
  const fetchEmailsQueued = useCallback(async () => {
    const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'tables', 'events', 'media', 'clients', 'notifications', 'calendar', 'music', 'stats', 'admins'] : (user?.allowedTabs || []);
    if (!allowed.includes('notifications')) { setEmailsQueued(0); return; }
    try {
      const now = Date.now();
      if (fetchEmailsQueued.last && (now - fetchEmailsQueued.last) < 30000) return;
      const ob = await adminAPI.get(`/notifications/outbox`);
      setEmailsQueued(ob.data?.total || (ob.data?.items?.length || 0) || 0);
      fetchEmailsQueued.last = now;
    } catch (e) {
      setEmailsQueued(0);
    }
  }, []);

  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [codes, setCodes] = useState([]);
  const [ordersEventId, setOrdersEventId] = useState('');
  const [tablesEventId, setTablesEventId] = useState('');
  const [ordersPolling, setOrdersPolling] = useState(false);
  const [ordersPollingError, setOrdersPollingError] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', guests: '2', eventVolume: '2', status: 'pending'
  });

  const [eventFormData, setEventFormData] = useState({
    volume: '', theme: '', date: '', time: '19H - 00H', price: '', status: 'upcoming', description: '', reservationsOpen: false,
    timeSlotsEnabled: false, slotDurationMinutes: ''
  });

  const [menuFormData, setMenuFormData] = useState({ dishes: [] });
  const [lineupFormData, setLineupFormData] = useState({ visible: true, artists: [] });
  const [reservationMenuOptions, setReservationMenuOptions] = useState([]);
  const [reservationMenuSelections, setReservationMenuSelections] = useState([]);
  const [composeIntent, setComposeIntent] = useState({ to: '', subject: '', open: false });
  const openComposeEmail = (to, subject = '') => {
    setComposeIntent({ to: to || '', subject: subject || '', open: true });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'tables', 'events', 'media', 'clients', 'notifications', 'calendar', 'music', 'stats', 'admins'] : (user?.allowedTabs || []);
      let reservationsRes = null;
      let statsRes = null;
      let clientsRes = null;
      const eventsRes = await adminAPI.get(`/events`);
      if (allowed.includes('reservations')) {
        reservationsRes = await adminAPI.get(`/reservations`);
        const normalizedReservations = (reservationsRes.data.reservations || []).map((r) => {
          const legacy = r.menuChoice === 'meat' || r.menuChoice === 'vegetarian';
          return { ...r, menuChoice: legacy ? '' : (r.menuChoice || '') };
        });
        setReservations(normalizedReservations);
      } else {
        setReservations([]);
      }
      if (allowed.includes('stats')) {
        statsRes = await adminAPI.get(`/events/stats`);
        setStats(statsRes.data);
      } else {
        setStats(null);
      }
      if (allowed.includes('clients')) {
        clientsRes = await adminAPI.get(`/clients`);
        setClients(clientsRes.data);
      } else {
        setClients(null);
      }
      setEvents(eventsRes.data.events || []);
      setNextEvent(eventsRes.data.nextEvent);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBootData = useCallback(async () => {
    setLoading(true);
    try {
      const eventsRes = await adminAPI.get(`/events`);
      setEvents(eventsRes.data.events || []);
      setNextEvent(eventsRes.data.nextEvent);
    } catch {
      setEvents([]);
      setNextEvent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSiteMediaConfig = useCallback(async () => {
    try {
      const res = await adminAPI.get(`/site-media/config`);
      const enabled = res.data?.enabled !== false;
      setSiteMediaEnabledState(enabled);
      try {
        localStorage.setItem('siteMediaEnabled', enabled ? 'true' : 'false');
      } catch {}
    } catch {
      try {
        const v = localStorage.getItem('siteMediaEnabled');
        setSiteMediaEnabledState(v === null ? true : v === 'true');
      } catch {
        setSiteMediaEnabledState(true);
      }
    }
  }, []);

  const setSiteMediaEnabled = useCallback(async (nextEnabled) => {
    const enabled = !!nextEnabled;
    const previous = siteMediaEnabled;

    setSiteMediaEnabledState(enabled);
    try {
      localStorage.setItem('siteMediaEnabled', enabled ? 'true' : 'false');
    } catch {}

    try {
      await adminAPI.put(`/site-media/config`, { id: 'global', enabled });
    } catch {
      setSiteMediaEnabledState(previous);
      try {
        localStorage.setItem('siteMediaEnabled', previous ? 'true' : 'false');
      } catch {}
      alert('Erreur lors de la mise a jour de la visibilite de la galerie');
    }
  }, [siteMediaEnabled]);

  useEffect(() => { fetchBootData(); }, [fetchBootData]);
  useEffect(() => { loadSiteMediaConfig(); }, [loadSiteMediaConfig]);
  const fetchReservations = useCallback(async (force = false) => {
    try {
      const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'tables', 'events', 'media', 'clients', 'notifications', 'calendar', 'music', 'stats', 'admins'] : (user?.allowedTabs || []);
      if (!allowed.includes('reservations')) { setReservations([]); return; }
      if (!force && reservationsLoaded) return;
      const res = await adminAPI.get(`/reservations`);
      const normalized = (res.data.reservations || []).map((r) => {
        const legacy = r.menuChoice === 'meat' || r.menuChoice === 'vegetarian';
        return { ...r, menuChoice: legacy ? '' : (r.menuChoice || '') };
      });
      setReservations(normalized);
      setReservationsLoaded(true);
    } catch {
      setReservations([]);
    }
  }, []);

  const fetchClientsOnly = useCallback(async (force = false) => {
    try {
      const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'tables', 'events', 'media', 'clients', 'notifications', 'calendar', 'music', 'stats', 'admins'] : (user?.allowedTabs || []);
      if (!allowed.includes('clients')) { setClients(null); return; }
      if (!force && clientsLoaded) return;
      const res = await adminAPI.get(`/clients`);
      setClients(res.data);
      setClientsLoaded(true);
    } catch {
      setClients(null);
    }
  }, []);

  const fetchStatsOnly = useCallback(async (force = false) => {
    try {
      const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'tables', 'events', 'media', 'clients', 'notifications', 'stats', 'admins'] : (user?.allowedTabs || []);
      if (!allowed.includes('stats')) { setStats(null); return; }
      if (!force && statsLoaded) return;
      const res = await adminAPI.get(`/events/stats`);
      setStats(res.data);
      setStatsLoaded(true);
    } catch {
      setStats(null);
    }
  }, []);

  useEffect(() => {
    const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'tables', 'events', 'media', 'clients', 'notifications', 'calendar', 'music', 'stats', 'admins'] : (user?.allowedTabs || []);
    if (adminActiveTab === 'reservations' && allowed.includes('reservations')) {
      fetchReservations();
    } else if (adminActiveTab === 'clients' && allowed.includes('clients')) {
      fetchClientsOnly();
    } else if (adminActiveTab === 'history' && allowed.includes('stats')) {
      fetchStatsOnly();
    }
  }, [adminActiveTab]);
  useEffect(() => {
    const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'tables', 'events', 'media', 'clients', 'notifications', 'calendar', 'music', 'stats', 'admins'] : (user?.allowedTabs || []);
    if (adminActiveTab === 'notifications' && allowed.includes('notifications') && !notificationsLoaded) {
      (async () => {
        await fetchEmailsUnread();
        await fetchEmailsQueued();
        setNotificationsLoaded(true);
      })();
    }
  }, [adminActiveTab, fetchEmailsUnread, fetchEmailsQueued, notificationsLoaded]);

  useEffect(() => {
    const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'tables', 'events', 'media', 'clients', 'notifications', 'calendar', 'music', 'stats', 'admins'] : (user?.allowedTabs || []);
    let timer = null;
    const run = async () => {
      await fetchEmailsUnread();
      await fetchEmailsQueued();
    };
    if (allowed.includes('notifications')) {
      run();
      timer = setInterval(run, 60000);
    } else {
      setEmailsUnread(0);
      setEmailsQueued(0);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [user, fetchEmailsUnread, fetchEmailsQueued]);

  useEffect(() => {
    let timer = null;
    let cancelled = false;
    let inFlight = false;
    let consecutiveFailures = 0;
    const pollOrders = async () => {
      if (!ordersEventId || inFlight) return;
      inFlight = true;
      try {
        const res = await adminAPI.get(`/orders`, { params: { eventId: ordersEventId } });
        if (!cancelled) {
          consecutiveFailures = 0;
          setOrdersPollingError(false);
          setOrders(res.data.orders || []);
        }
      } catch (error) {
        console.error('Error polling orders:', error);
        if (!cancelled) {
          consecutiveFailures += 1;
          if (consecutiveFailures >= 2) {
            setOrdersPollingError(true);
          }
        }
      } finally {
        inFlight = false;
      }
    };
    const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'events', 'media', 'clients', 'notifications', 'stats', 'admins'] : (user?.allowedTabs || []);
    if (adminActiveTab === 'orders' && ordersEventId && allowed.includes('orders')) {
      setOrdersPolling(true);
      void pollOrders();
      timer = setInterval(pollOrders, 5000);
    } else {
      setOrdersPolling(false);
      setOrdersPollingError(false);
    }
    return () => {
      cancelled = true;
      inFlight = false;
      setOrdersPolling(false);
      if (timer) clearInterval(timer);
    };
  }, [adminActiveTab, ordersEventId, user]);
  useEffect(() => {
    const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'tables', 'events', 'media', 'clients', 'notifications', 'calendar', 'music', 'stats', 'admins'] : (user?.allowedTabs || []);
    if (adminActiveTab === 'tables' && tablesEventId && allowed.includes('tables')) {
      fetchTables(tablesEventId);
    }
  }, [adminActiveTab, tablesEventId]);
  useEffect(() => {
    const allowed = user?.role === 'owner' ? ['codes', 'reservations', 'orders', 'tables', 'events', 'media', 'clients', 'notifications', 'calendar', 'music', 'stats', 'admins'] : (user?.allowedTabs || []);
    if (adminActiveTab === 'codes' && allowed.includes('codes')) {
      fetchCodes();
    }
  }, [adminActiveTab]);
  useEffect(() => {
    if (isSelectionModalOpen && selectionTab === 'uploaded') {
      loadSelectionList();
    }
  }, [isSelectionModalOpen, selectionTab, selectionFilter, selectionQuery]);

  useEffect(() => {
    const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'tables', 'events', 'media', 'clients', 'notifications', 'calendar', 'music', 'stats', 'admins'] : (user?.allowedTabs || []);
    if (adminActiveTab === 'media' && allowed.includes('media')) {
      if (!mediaLoaded) {
        fetchLibrary();
        fetchFeaturedMedia();
        setMediaLoaded(true);
      } else {
        fetchLibrary();
      }
    }
  }, [adminActiveTab, libraryFilter, libraryQuery, librarySort, mediaLoaded]);

  useEffect(() => {
    const loadMenuForReservation = async () => {
      const vol = parseInt(formData.eventVolume);
      const ev = events.find(e => e.volume === vol);
      if (!ev) {
        setReservationMenuOptions([]);
        return;
      }
      try {
        const res = await adminAPI.get(`/menus/${ev.id}`);
        const dishes = res.data.menu?.dishes || [];
        setReservationMenuOptions(dishes);
        setReservationMenuSelections(prev => {
          const names = new Set(dishes.map(d => d.name));
          return prev.filter(s => names.has(s.name));
        });
      } catch {
        setReservationMenuOptions([]);
        setReservationMenuSelections([]);
      }
    };
    if (events.length) loadMenuForReservation();
  }, [formData.eventVolume, events]);

  const toggleReservations = async (eventId, currentStatus) => {
    setUpdatingEvent(true);
    try {
      const newStatus = !currentStatus;
      await adminAPI.put(`/events/${eventId}`, {
        reservationsOpen: newStatus,
        status: newStatus ? 'available' : 'upcoming'
      });
      fetchData();
    } catch (error) {
      console.error('Error updating event:', error);
    } finally {
      setUpdatingEvent(false);
    }
  };

  const fetchTables = async (eventId) => {
    try {
      const res = await adminAPI.get(`/tables`, { params: { eventId } });
      setTables(res.data.tables || []);
    } catch {
      setTables([]);
    }
  };

  const fetchCodes = async () => {
    try {
      const res = await adminAPI.get(`/codes`);
      setCodes(res.data.codes || []);
    } catch {
      setCodes([]);
    }
  };

  const fetchOrders = async (eventId) => {
    try {
      const res = await adminAPI.get(`/orders`, { params: { eventId } });
      setOrdersPollingError(false);
      setOrders(res.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const updateOrder = async (eventId, orderId, fields) => {
    try {
      await adminAPI.put(`/orders/${orderId}`, fields);
      await fetchOrders(eventId);
    } catch (e) {
      alert('Erreur lors de la mise à jour de la commande');
    }
  };

  const finishOrder = async (eventId, orderId) => {
    try {
      await adminAPI.post(`/orders/${orderId}/finish`);
      await fetchOrders(eventId);
    } catch (e) {
      alert('Erreur lors de la finalisation de la commande');
    }
  };
  const payOrder = async (eventId, orderId) => {
    try {
      await adminAPI.post(`/orders/${orderId}/pay`);
      await fetchOrders(eventId);
    } catch (e) {
      alert('Erreur lors du paiement de la commande');
    }
  };

  const deleteOrder = async (eventId, orderId) => {
    try {
      await adminAPI.delete(`/orders/${orderId}`);
      await fetchOrders(eventId);
    } catch (e) {
      alert('Erreur lors de la suppression de la commande');
    }
  };

  const createTablesBulk = async (eventId, count, baseName) => {
    try {
      await adminAPI.post(`/tables/bulk`, { eventId, count, baseName });
      await fetchTables(eventId);
    } catch (e) {
      alert('Erreur lors de la création des tables');
    }
  };

  const createTable = async (eventId, name, number, link, codeId = null) => {
    try {
      await adminAPI.post(`/tables`, { eventId, name, number, link, codeId: codeId || undefined });
      await fetchTables(eventId);
    } catch (e) {
      alert('Erreur lors de la création de la table');
    }
  };
  const updateTable = async (eventId, tableId, fields) => {
    const optimistic = typeof fields === 'object' && fields;
    if (optimistic) {
      try {
        setTables(prev => prev.map(t => t.id === tableId ? { ...t, ...fields } : t));
      } catch { }
    }
    try {
      await adminAPI.put(`/tables/${tableId}`, fields);
      await fetchTables(eventId);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur lors de la mise à jour de la table';
      alert(msg);
      try { await fetchTables(eventId); } catch { }
    }
  };

  const createCode = async (payload) => {
    try {
      await adminAPI.post(`/codes`, payload);
      await fetchCodes();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur lors de la création du code';
      alert(msg);
    }
  };

  const updateCode = async (id, fields) => {
    try {
      await adminAPI.put(`/codes/${id}`, fields);
      await fetchCodes();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur lors de la mise à jour du code';
      alert(msg);
    }
  };

  const deleteCode = async (id) => {
    try {
      await adminAPI.delete(`/codes/${id}`);
      await fetchCodes();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur lors de la suppression du code';
      alert(msg);
    }
  };
  const deleteTable = async (eventId, tableId) => {
    try {
      await adminAPI.delete(`/tables/${tableId}`);
      await fetchTables(eventId);
    } catch (e) {
      alert('Erreur lors de la suppression de la table');
    }
  };
  const handleAddReservation = async (e) => {
    e.preventDefault();
    try {
      const guests = parseInt(formData.guests);
      const eventVolume = Number.isFinite(parseInt(formData.eventVolume))
        ? parseInt(formData.eventVolume)
        : (nextEvent?.volume || 1);
      const phone = (formData.phone || '').trim();
      if (phone.length < 8) {
        alert('Le numéro de téléphone doit contenir au moins 8 caractères');
        return;
      }
      await adminAPI.post(`/reservations`, {
        name: formData.name,
        email: formData.email,
        phone,
        guests: Number.isFinite(guests) ? guests : 1,
        eventVolume,
        menuSelections: reservationMenuSelections.filter(s => s.quantity > 0),
      });
      setIsAddModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      alert('Erreur lors de la création');
    }
  };

  const handleEditReservation = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.put(`/reservations/${selectedReservation.id}`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        guests: parseInt(formData.guests),
        eventVolume: parseInt(formData.eventVolume),
        status: formData.status,
        menuSelections: reservationMenuSelections.filter(s => s.quantity > 0),
      });
      setIsEditModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteReservation = async () => {
    try {
      await adminAPI.delete(`/reservations/${selectedReservation.id}`);
      setIsDeleteModalOpen(false);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        volume: parseInt(eventFormData.volume), theme: eventFormData.theme, date: eventFormData.date,
        time: eventFormData.time, price: eventFormData.price ? parseInt(eventFormData.price) : null,
        status: eventFormData.status, description: eventFormData.description, reservationsOpen: eventFormData.reservationsOpen,
        timeSlotsEnabled: !!eventFormData.timeSlotsEnabled,
        slotDurationMinutes: eventFormData.slotDurationMinutes ? parseInt(eventFormData.slotDurationMinutes) : null
      };
      if (selectedEvent) {
        await adminAPI.put(`/events/${selectedEvent.id}`, eventData);
      } else {
        await adminAPI.post(`/events`, eventData);
      }
      setIsEventModalOpen(false);
      resetEventForm();
      fetchData();
    } catch (error) {
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try {
      await adminAPI.delete(`/events/${eventId}`);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleUploadMedia = async (files) => {
    if (!selectedEvent || !files || files.length === 0) return;
    setUploadingMedia(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append('eventId', selectedEvent.id);
        form.append('file', file);
        form.append('visible', 'true');
        await adminAPI.post(`/media/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      const res = await adminAPI.get(`/media/${selectedEvent.id}`);
      setMediaItems(res.data.media || []);
    } catch (error) {
      alert('Erreur lors du téléversement');
    } finally {
      setUploadingMedia(false);
    }
  };

  const updateMedia = async (mediaId, fields) => {
    try {
      const res = await adminAPI.put(`/media/${mediaId}`, fields);
      setMediaItems(prev => prev.map(m => m.id === mediaId ? res.data : m));
    } catch (error) {
      alert('Erreur lors de la mise à jour du média');
    }
  };

  const deleteMedia = async (mediaId) => {
    try {
      await adminAPI.delete(`/media/${mediaId}`);
      setMediaItems(prev => prev.filter(m => m.id !== mediaId));
    } catch (error) {
      alert('Erreur lors de la suppression du média');
    }
  };

  const moveMedia = async (index, direction) => {
    if (!selectedEvent) return;
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= mediaItems.length) return;
    const items = [...mediaItems];
    const tmp = items[index];
    items[index] = items[newIndex];
    items[newIndex] = tmp;
    const reordered = items.map((item, i) => ({ id: item.id, order: i }));
    try {
      await adminAPI.post(`/media/reorder/${selectedEvent.id}`, reordered);
      setMediaItems(items.map((item, i) => ({ ...item, order: i })));
    } catch (error) {
      alert('Erreur lors du réordonnancement');
    }
  };

  const openDeleteClientModal = (client) => {
    setSelectedClient(client);
    setIsDeleteClientModalOpen(true);
  };

  const handleDeleteClient = async () => {
    if (!selectedClient) return;
    try {
      await adminAPI.delete(`/clients/${encodeURIComponent(selectedClient.email)}`);
      setIsDeleteClientModalOpen(false);
      setSelectedClient(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Erreur lors de la suppression du client');
    }
  };

  const handleSaveMenu = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.post(`/menus`, { eventId: selectedEvent.id, dishes: menuFormData.dishes });
      setIsMenuModalOpen(false);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la sauvegarde du menu');
    }
  };

  const openEventModal = (event = null) => {
    if (event) {
      setSelectedEvent(event);
      setEventFormData({
        volume: event.volume.toString(), theme: event.theme, date: event.date,
        time: event.time || '19H - 00H', price: event.price?.toString() || '',
        status: event.status, description: event.description || '', reservationsOpen: event.reservationsOpen || false
      });
    } else {
      setSelectedEvent(null);
      resetEventForm();
    }
    setIsEventModalOpen(true);
  };

  const openMenuModal = async (event) => {
    setSelectedEvent(event);
    try {
      const res = await adminAPI.get(`/menus/${event.id}`);
      setMenuFormData({ dishes: res.data.menu?.dishes || [] });
    } catch (error) {
      setMenuFormData({ dishes: [] });
    }
    setIsMenuModalOpen(true);
  };

  const openMediaModal = async (event) => {
    setSelectedEvent(event);
    try {
      const res = await adminAPI.get(`/media/${event.id}`);
      setMediaItems(res.data.media || []);
    } catch (error) {
      setMediaItems([]);
    }
    setIsMediaModalOpen(true);
  };

  const openLineupModal = async (event) => {
    setSelectedEvent(event);
    try {
      const res = await adminAPI.get(`/lineup/${event.id}`);
      setLineupFormData({ visible: !!res.data.visible, artists: res.data.artists || [] });
    } catch (error) {
      setLineupFormData({ visible: true, artists: [] });
    }
    setIsLineupModalOpen(true);
  };

  const handleSaveLineup = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    try {
      await adminAPI.post(`/lineup`, { eventId: selectedEvent.id, visible: lineupFormData.visible, artists: lineupFormData.artists });
      fetchData();
      setIsLineupModalOpen(false);
    } catch (error) {
      alert('Erreur lors de la sauvegarde du line-up');
    }
  };

  const addArtist = () => {
    setLineupFormData(prev => ({ artists: [...prev.artists, { name: '', role: '', startTime: '', endTime: '', soundcloudUrl: '', spotifyUrl: '', instagram: '', facebook: '', otherLink: '', description: '', avatarUrl: '' }], visible: prev.visible }));
  };
  const updateArtist = (index, field, value) => {
    setLineupFormData(prev => ({
      visible: prev.visible,
      artists: prev.artists.map((a, i) => i === index ? { ...a, [field]: value } : a)
    }));
  };
  const removeArtist = (index) => {
    setLineupFormData(prev => ({ visible: prev.visible, artists: prev.artists.filter((_, i) => i !== index) }));
  };

  const fetchFeaturedMedia = async () => {
    const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'events', 'media', 'clients', 'notifications', 'stats', 'admins'] : (user?.allowedTabs || []);
    if (!allowed.includes('media')) { setFeaturedItems([]); return; }
    setFeaturedLoading(true);
    try {
      const res = await adminAPI.get(`/featured-media`);
      const items = (res.data.items || []).map(i => ({
        id: i.featured.id,
        mediaId: i.featured.mediaId,
        order: i.featured.order,
        priority: i.featured.priority,
        durationSeconds: i.featured.durationSeconds,
        visible: i.featured.visible,
        media: i.media,
      }));
      setFeaturedItems(items);
    } catch (error) {
      setFeaturedItems([]);
    } finally {
      setFeaturedLoading(false);
    }
  };

  const openFeaturedModal = async () => {
    await fetchFeaturedMedia();
    setIsFeaturedModalOpen(true);
  };

  const fetchLibrary = async () => {
    const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'events', 'media', 'clients', 'notifications', 'stats', 'admins'] : (user?.allowedTabs || []);
    if (!allowed.includes('media')) { setLibraryList([]); return; }
    setLibraryLoading(true);
    try {
      const params = [];
      if (libraryFilter !== 'all') params.push(`type=${libraryFilter}`);
      if (libraryQuery) params.push(`q=${encodeURIComponent(libraryQuery)}`);
      params.push(`sort=${encodeURIComponent(librarySort || '-createdAt')}`);
      const res = await adminAPI.get(`/media/all${params.length ? `?${params.join('&')}` : ''}`);
      let items = res.data.media || [];
      if (!items || items.length === 0) {
        try {
          const eventsRes = await adminAPI.get(`/events`);
          const eventIds = (eventsRes.data.events || []).map(ev => ev.id);
          const ids = [...eventIds, 'global'];
          const medias = await Promise.all(ids.map(id => adminAPI.get(`/media/${id}`).catch(() => ({ data: { media: [] } }))));
          items = medias.flatMap(m => m.data.media || []);
        } catch (aggErr) {
          items = [];
        }
      }
      setLibraryList(items);
    } catch (error) {
      try {
        const eventsRes = await adminAPI.get(`/events`);
        const eventIds = (eventsRes.data.events || []).map(ev => ev.id);
        const ids = [...eventIds, 'global'];
        const medias = await Promise.all(ids.map(id => adminAPI.get(`/media/${id}`).catch(() => ({ data: { media: [] } }))));
        const items = medias.flatMap(m => m.data.media || []);
        setLibraryList(items);
      } catch (aggErr) {
        setLibraryList([]);
      }
    } finally {
      setLibraryLoading(false);
    }
  };

  const updateLibraryMedia = async (mediaId, fields) => {
    try {
      const res = await adminAPI.put(`/media/${mediaId}`, fields);
      setLibraryList(prev => prev.map(m => m.id === mediaId ? res.data : m));
    } catch (error) {
      alert('Erreur lors de la mise à jour des métadonnées');
    }
  };

  const addSelectedToFeatured = async () => {
    if (selectionSelectedIds.length === 0) return;
    try {
      for (const mid of selectionSelectedIds) {
        await adminAPI.post(`/featured-media`, { mediaId: mid, priority: 0, durationSeconds: 5, visible: true });
      }
      await fetchFeaturedMedia();
      setIsSelectionModalOpen(false);
      setSelectionSelectedIds([]);
    } catch (error) {
      alert('Erreur lors de l\'ajout au featured');
    }
  };

  const deleteFeaturedItem = async (fid) => {
    try {
      await adminAPI.delete(`/featured-media/${fid}`);
      setFeaturedItems(prev => prev.filter(f => f.id !== fid));
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const updateFeaturedItem = async (fid, fields) => {
    try {
      await adminAPI.put(`/featured-media/${fid}`, fields);
      setFeaturedItems(prev => prev.map(f => f.id === fid ? { ...f, ...fields } : f));
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const moveFeatured = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= featuredItems.length) return;
    const items = [...featuredItems];
    const tmp = items[index];
    items[index] = items[newIndex];
    items[newIndex] = tmp;
    const reordered = items.map((item, i) => ({ id: item.id, order: i }));
    try {
      await adminAPI.post(`/featured-media/reorder`, reordered);
      setFeaturedItems(items.map((item, i) => ({ ...item, order: i })));
    } catch (error) {
      alert('Erreur lors du réordonnancement');
    }
  };

  const openSelectionModal = (mode = 'featured', eventId = '') => {
    setSelectionMode(mode);
    setSelectionEventId(eventId || (selectedEvent?.id || ''));
    setSelectionTab('uploaded');
    setSelectionFilter('all');
    setSelectionQuery('');
    setSelectionSelectedIds([]);
    setIsSelectionModalOpen(true);
    loadSelectionList();
  };

  const loadSelectionList = async () => {
    const allowed = user?.role === 'owner' ? ['reservations', 'orders', 'events', 'media', 'clients', 'notifications', 'stats', 'admins'] : (user?.allowedTabs || []);
    if (!allowed.includes('media')) { setSelectionList([]); return; }
    setSelectionLoading(true);
    try {
      const params = [];
      if (selectionFilter !== 'all') params.push(`type=${selectionFilter}`);
      if (selectionQuery) params.push(`q=${encodeURIComponent(selectionQuery)}`);
      const res = await adminAPI.get(`/media/all${params.length ? `?${params.join('&')}` : ''}`);
      let items = res.data.media || [];
      if (!items || items.length === 0) {
        try {
          const eventsRes = await adminAPI.get(`/events`);
          const eventIds = (eventsRes.data.events || []).map(ev => ev.id);
          const ids = [...eventIds, 'global'];
          const medias = await Promise.all(ids.map(id => adminAPI.get(`/media/${id}`).catch(() => ({ data: { media: [] } }))));
          items = medias.flatMap(m => m.data.media || []);
        } catch (aggErr) {
          items = [];
        }
      }
      setSelectionList(items);
    } catch (error) {
      try {
        const eventsRes = await adminAPI.get(`/events`);
        const eventIds = (eventsRes.data.events || []).map(ev => ev.id);
        const ids = [...eventIds, 'global'];
        const medias = await Promise.all(ids.map(id => adminAPI.get(`/media/${id}`).catch(() => ({ data: { media: [] } }))));
        const items = medias.flatMap(m => m.data.media || []);
        setSelectionList(items);
      } catch (aggErr) {
        setSelectionList([]);
      }
    } finally {
      setSelectionLoading(false);
    }
  };

  const toggleSelection = (mid) => {
    setSelectionSelectedIds(prev => prev.includes(mid) ? prev.filter(id => id !== mid) : [...prev, mid]);
  };

  const addSelectedToEvent = async () => {
    if (!selectionEventId || selectionSelectedIds.length === 0) return;
    try {
      for (const mid of selectionSelectedIds) {
        await adminAPI.post(`/media/copy`, { sourceMediaId: mid, targetEventId: selectionEventId });
      }
      if (selectedEvent && selectedEvent.id === selectionEventId) {
        const res = await adminAPI.get(`/media/${selectedEvent.id}`);
        setMediaItems(res.data.media || []);
      }
      setIsSelectionModalOpen(false);
    } catch (error) {
      alert('Erreur lors de l\'ajout au média de l\'événement');
    }
  };

  const toggleEventMediaActive = async () => {
    if (!selectedEvent) return;
    setMediaActiveUpdating(true);
    try {
      const nextActive = !selectedEvent.mediaActive;
      await adminAPI.put(`/events/${selectedEvent.id}`, { mediaActive: nextActive });
      setSelectedEvent(prev => ({ ...prev, mediaActive: nextActive }));
      setEvents(prev => prev.map(e => e.id === selectedEvent.id ? { ...e, mediaActive: nextActive } : e));
      setNextEvent(prev => (prev && prev.id === selectedEvent.id) ? { ...prev, mediaActive: nextActive } : prev);
      setMediaActiveMessage(nextActive ? 'Médias activés pour cet événement' : 'Médias désactivés pour cet événement');
      setTimeout(() => setMediaActiveMessage(''), 2000);
    } catch (error) {
      setMediaActiveMessage('Erreur lors de la mise à jour du statut des médias');
    } finally {
      setMediaActiveUpdating(false);
    }
  };

  const openEditModal = (reservation) => {
    setSelectedReservation(reservation);
    setFormData({
      name: reservation.name, email: reservation.email, phone: reservation.phone,
      guests: reservation.guests.toString(),
      eventVolume: reservation.eventVolume.toString(), status: reservation.status
    });
    setReservationMenuSelections((reservation.menuSelections || []).map(s => ({ name: s.name, quantity: s.quantity })));
    setIsEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', guests: '2', eventVolume: '2', status: 'pending' });
    setSelectedReservation(null);
    setReservationMenuSelections([]);
  };

  const resetEventForm = () => {
    setEventFormData({ volume: '', theme: '', date: '', time: '19H - 00H', price: '', status: 'upcoming', description: '', reservationsOpen: false });
    setSelectedEvent(null);
  };

  const copyEmailsToClipboard = async () => {
    if (!clients) return;
    try {
      await navigator.clipboard.writeText(clients.emailsFormatted);
      setCopiedEmails(true);
      setTimeout(() => setCopiedEmails(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const addDish = () => {
    setMenuFormData(prev => ({
      dishes: [...prev.dishes, { name: '', type: 'main', vegetarian: false, meat: false, spicy: false, fish: false, alcoholic: false, nonAlcoholic: false, ingredients: [], description: '', price: 0 }]
    }));
  };

  const addDishOfType = (type) => {
    const t = ['starter', 'main', 'dessert', 'drink'].includes(type) ? type : 'main';
    setMenuFormData(prev => ({
      dishes: [...prev.dishes, { name: '', type: t, vegetarian: false, meat: false, spicy: false, fish: false, alcoholic: false, nonAlcoholic: false, ingredients: [], description: '', price: 0 }]
    }));
  };

  const updateDish = (index, field, value) => {
    setMenuFormData(prev => ({
      dishes: prev.dishes.map((dish, i) => i === index ? { ...dish, [field]: value } : dish)
    }));
  };

  const setMenuSelectionQuantity = (name, quantity) => {
    setReservationMenuSelections(prev => {
      const existing = prev.find(s => s.name === name);
      if (existing) {
        return prev.map(s => s.name === name ? { name, quantity: Math.max(0, parseInt(quantity) || 0) } : s);
      }
      return [...prev, { name, quantity: Math.max(0, parseInt(quantity) || 0) }];
    });
  };

  const removeDish = (index) => {
    setMenuFormData(prev => ({ dishes: prev.dishes.filter((_, i) => i !== index) }));
  };

  const addIngredient = (dishIndex) => {
    setMenuFormData(prev => ({
      dishes: prev.dishes.map((dish, i) =>
        i === dishIndex
          ? { ...dish, ingredients: [...(dish.ingredients || []), ''] }
          : dish
      )
    }));
  };

  const updateIngredient = (dishIndex, ingredientIndex, value) => {
    setMenuFormData(prev => ({
      dishes: prev.dishes.map((dish, i) =>
        i === dishIndex
          ? {
            ...dish,
            ingredients: dish.ingredients.map((ing, j) => j === ingredientIndex ? value : ing)
          }
          : dish
      )
    }));
  };

  const removeIngredient = (dishIndex, ingredientIndex) => {
    setMenuFormData(prev => ({
      dishes: prev.dishes.map((dish, i) =>
        i === dishIndex
          ? {
            ...dish,
            ingredients: dish.ingredients.filter((_, j) => j !== ingredientIndex)
          }
          : dish
      )
    }));
  };

  const filteredReservations = reservations.filter(res => {
    const matchesSearch = res.name.toLowerCase().includes(searchTerm.toLowerCase()) || res.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || res.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-green-500/20 text-green-400', pending: 'bg-yellow-500/20 text-yellow-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    const labels = { confirmed: 'Confirmé', pending: 'En attente', cancelled: 'Annulé' };
    return <Badge className={styles[status] || ''}>{labels[status] || status}</Badge>;
  };

  const deleteLibraryMedia = async (mediaId) => {
    try {
      await adminAPI.delete(`/media/${mediaId}`);
      setLibraryList(prev => prev.filter(x => x.id !== mediaId));
    } catch (e) {
      alert('Erreur lors de la suppression');
    }
  };

  const bulkDeleteLibrarySelected = async () => {
    if (!librarySelectedIds.length) return;
    try {
      await adminAPI.post(`/media/bulk-delete`, librarySelectedIds);
      await fetchLibrary();
      setLibrarySelectedIds([]);
    } catch (e) {
      alert('Erreur lors de la suppression en masse');
    }
  };

  const value = {
    // auth
    isAuthenticated, authLoading, logout, user,
    // datasets
    reservations, stats, clients, events, nextEvent, tables, orders,
    codes,
    // ui state
    loading, searchTerm, statusFilter, copiedEmails, updatingEvent,
    setSearchTerm, setStatusFilter,
    adminActiveTab, setAdminActiveTab,
    sidebarOpen, setSidebarOpen,
    // modals
    isAddModalOpen, setIsAddModalOpen,
    isEditModalOpen, setIsEditModalOpen,
    isDeleteModalOpen, setIsDeleteModalOpen,
    isEventModalOpen, setIsEventModalOpen,
    isMenuModalOpen, setIsMenuModalOpen,
    isMediaModalOpen, setIsMediaModalOpen,
    isLineupModalOpen, setIsLineupModalOpen,
    isDeleteClientModalOpen, setIsDeleteClientModalOpen,
    selectedReservation, setSelectedReservation,
    selectedEvent, setSelectedEvent,
    selectedClient, setSelectedClient,
    // library
    libraryList, libraryLoading, libraryFilter, libraryQuery, librarySort, librarySelectedIds,
    setLibraryFilter, setLibraryQuery, setLibrarySort, setLibrarySelectedIds,
    // featured
    isFeaturedModalOpen, setIsFeaturedModalOpen, featuredItems, featuredLoading,
    // media
    mediaItems, uploadingMedia, mediaActiveUpdating, mediaActiveMessage,
    // selection
    isSelectionModalOpen, setIsSelectionModalOpen,
    selectionMode, setSelectionMode,
    selectionEventId, setSelectionEventId,
    selectionTab, setSelectionTab,
    selectionFilter, setSelectionFilter,
    selectionQuery, setSelectionQuery,
    selectionList, selectionLoading, selectionSelectedIds,
    setSelectionSelectedIds,
    uploadProgress, setUploadProgress,
    // site settings
    siteMediaEnabled, setSiteMediaEnabled,
    // forms
    formData, setFormData,
    eventFormData, setEventFormData,
    menuFormData, setMenuFormData,
    lineupFormData, setLineupFormData,
    reservationMenuOptions, reservationMenuSelections, setMenuSelectionQuantity,
    composeIntent, setComposeIntent, openComposeEmail,
    // notifications
    emailsUnread,
    setEmailsUnread,
    fetchEmailsUnread,
    emailsQueued,
    setEmailsQueued,
    fetchEmailsQueued,
    // actions
    fetchBootData,
    fetchData,
    fetchReservations,
    fetchClientsOnly,
    fetchStatsOnly,
    fetchTables,
    fetchOrders,
    fetchCodes,
    createTablesBulk,
    createTable,
    updateTable,
    deleteTable,
    createCode,
    updateCode,
    deleteCode,
    updateOrder,
    finishOrder,
    deleteOrder,
    payOrder,
    toggleReservations,
    handleAddReservation,
    handleEditReservation,
    handleDeleteReservation,
    handleSaveEvent,
    handleDeleteEvent,
    handleUploadMedia,
    updateMedia,
    deleteMedia,
    moveMedia,
    openDeleteClientModal,
    handleDeleteClient,
    handleSaveMenu,
    openEventModal,
    openMenuModal,
    openMediaModal,
    openLineupModal,
    fetchFeaturedMedia,
    openFeaturedModal,
    fetchLibrary,
    updateLibraryMedia,
    addSelectedToFeatured,
    deleteFeaturedItem,
    updateFeaturedItem,
    moveFeatured,
    openSelectionModal,
    loadSelectionList,
    toggleSelection,
    addSelectedToEvent,
    toggleEventMediaActive,
    openEditModal,
    resetForm,
    resetEventForm,
    copyEmailsToClipboard,
    addDish,
    addDishOfType,
    updateDish,
    removeDish,
    addIngredient,
    updateIngredient,
    removeIngredient,
    handleSaveLineup,
    addArtist,
    updateArtist,
    removeArtist,
    filteredReservations,
    getStatusBadge,
    mediaSrc,
    deleteLibraryMedia,
    bulkDeleteLibrarySelected,
    ordersEventId, setOrdersEventId, ordersPolling, ordersPollingError,
    tablesEventId, setTablesEventId,
  };

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
};

AdminProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Access Admin context state and actions.
 * @returns {object} Admin state/actions
 */
export const useAdmin = () => {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
};

export default useAdmin;
